import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card } from '../components/ui/Card';
import { trpc } from '../lib/trpc';
import { showToast } from '../utils/toast';
import { validateAge } from '@wineryxplorer/shared';
import { OnboardingNavButtons } from '../components/onboarding/OnboardingNavButtons';
import { OnboardingProgress } from '../components/onboarding/OnboardingProgress';
import { OnboardingStepLocation } from '../components/onboarding/OnboardingStepLocation';
import { OnboardingStepPhoto } from '../components/onboarding/OnboardingStepPhoto';
import { OnboardingStepPreferences } from '../components/onboarding/OnboardingStepPreferences';
import { useAuth } from '../hooks/useAuth';
import type { AvatarEditorHandle } from '../components/AvatarEditor';
import type {
  OnboardingFormData as FormData,
  OnboardingFormErrors as FormErrors,
} from '../components/onboarding/types';

type OnboardingStep = 1 | 2 | 3;

export const Onboarding = () => {
  const navigate = useNavigate();
  const { refetchUser } = useAuth();
  const [currentStep, setCurrentStep] = useState<OnboardingStep>(1);
  const [formData, setFormData] = useState<FormData>({
    birthday: '',
    street: '',
    city: '',
    state: '',
    postalCode: '',
    country: '',
    winePreferencesText: '',
    regionPreference: '',
    venuePreferencesText: '',
  });
  const [errors, setErrors] = useState<FormErrors>({});
  const [avatarRemoved, setAvatarRemoved] = useState(false);
  const avatarEditorRef = useRef<AvatarEditorHandle>(null);

  // tRPC mutations and queries
  const submitProfileMutation = trpc.onboarding.submitProfile.useMutation();
  const { data: currentUser } = trpc.user.getMe.useQuery();
  const utils = trpc.useUtils();

  const isLoading = submitProfileMutation.isPending;

  // Pre-populate form with existing user data.
  //
  // Date of birth is now captured and validated server-side at registration,
  // so it is already on the record by the time anyone reaches this step.
  // Carrying it over spares people typing it twice; it still has to be
  // confirmed here, and submitProfile re-validates it against the country
  // chosen below — a value arriving from the client is never trusted as-is.
  useEffect(() => {
    if (currentUser) {
      setFormData((prev) => ({
        ...prev,
        birthday: prev.birthday || currentUser.birthday?.slice(0, 10) || '',
      }));
    }
  }, [currentUser]);

  // Validation functions
  const validateStep1 = (): boolean => {
    const newErrors: FormErrors = {};

    if (!formData.birthday) {
      newErrors.birthday = 'Birthdate is required';
    } else if (formData.country) {
      // Validate age using shared validation utility
      const ageError = validateAge(new Date(formData.birthday), formData.country);
      if (ageError) {
        newErrors.birthday = ageError;
      }
    }

    if (!formData.city.trim()) {
      newErrors.city = 'City is required';
    }

    if (!formData.state.trim()) {
      newErrors.state = 'State/Province is required';
    }

    if (!formData.country.trim()) {
      newErrors.country = 'Country is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Real-time validation helpers
  const validateBirthday = (birthdayValue: string, countryValue: string) => {
    if (!birthdayValue) {
      setErrors((prev) => ({ ...prev, birthday: undefined }));
      return;
    }
    if (countryValue) {
      const ageError = validateAge(new Date(birthdayValue), countryValue);
      if (ageError) {
        setErrors((prev) => ({ ...prev, birthday: ageError }));
      } else {
        setErrors((prev) => ({ ...prev, birthday: undefined }));
      }
    }
  };

  const validateCity = (cityValue: string) => {
    if (!cityValue.trim()) {
      setErrors((prev) => ({ ...prev, city: undefined }));
    } else {
      setErrors((prev) => ({ ...prev, city: undefined }));
    }
  };

  const validateState = (stateValue: string) => {
    if (!stateValue.trim()) {
      setErrors((prev) => ({ ...prev, state: undefined }));
    } else {
      setErrors((prev) => ({ ...prev, state: undefined }));
    }
  };

  const validateCountry = (countryValue: string) => {
    if (!countryValue.trim()) {
      setErrors((prev) => ({ ...prev, country: undefined }));
    } else {
      setErrors((prev) => ({ ...prev, country: undefined }));
    }
  };

  // Handle field changes
  const handleChange = (field: keyof FormData, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    // Clear error for this field
    if (field in errors) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[field as keyof FormErrors];
        return newErrors;
      });
    }
  };

  // Detect if user signed up via OAuth (Google/Apple) - they have an image from the provider
  const isOAuthUser = Boolean(currentUser?.image);
  
  // OAuth users skip step 3 (avatar) - total steps is 2 for OAuth, 3 for email/password
  const totalSteps = isOAuthUser ? 2 : 3;

  // Navigation handlers
  const handleNext = () => {
    if (currentStep === 1 && !validateStep1()) {
      return;
    }
    if (currentStep < totalSteps) {
      setCurrentStep((prev) => (prev + 1) as OnboardingStep);
    }
  };

  const handleBack = () => {
    if (currentStep > 1) {
      setCurrentStep((prev) => (prev - 1) as OnboardingStep);
    }
  };

  const handleSkip = () => {
    if (currentStep === 2 && !isOAuthUser) {
      setCurrentStep(3);
    } else {
      handleSubmit();
    }
  };

  // Submit handler
  const handleSubmit = async () => {
    // Final validation
    if (!validateStep1()) {
      showToast('Please complete all required fields', 'error');
      setCurrentStep(1);
      return;
    }

    // If the user selected an avatar but never clicked Confirm in the cropper,
    // commit the pending crop before finishing onboarding. Otherwise the
    // updateAvatar mutation never fires and the uploaded image is lost.
    if (avatarEditorRef.current?.hasPendingCrop()) {
      try {
        await avatarEditorRef.current.commitPendingCrop();
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Failed to save avatar';
        showToast(message, 'error');
        return;
      }
    }

    try {
      await submitProfileMutation.mutateAsync({
        birthday: formData.birthday,
        city: formData.city,
        state: formData.state,
        country: formData.country,
        street: formData.street || undefined,
        postalCode: formData.postalCode || undefined,
        winePreferences: formData.winePreferencesText || undefined,
        regionPreference: formData.regionPreference || undefined,
        venuePreferences: formData.venuePreferencesText || undefined,
      });

      // Refetch user so OnboardingGuard sees onboardingComplete=true before we navigate.
      // Without this, the cached user still has onboardingComplete=false and any
      // guarded route (e.g. /profile) bounces back to /onboarding.
      await utils.user.getMe.invalidate();

      showToast('Profile completed successfully!', 'success');
      navigate('/');
    } catch (error: unknown) {
      if (error instanceof Error) {
        showToast(error.message, 'error');

        // If age verification failed, go back to step 1
        if (error.message.includes('21 years old') || error.message.includes('16 years old')) {
          setCurrentStep(1);
        }
      } else {
        showToast('Failed to complete onboarding', 'error');
      }
    }
  };

  // Progress indicator
  const steps = isOAuthUser
    ? [{ label: 'Birthday & Location' }, { label: 'Preferences' }]
    : [{ label: 'Birthday & Location' }, { label: 'Preferences' }, { label: 'Photo' }];

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4 py-12">
      <div className="w-full max-w-2xl">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">
            Complete Your Profile
          </h1>
          <p className="text-gray-600">
            Let's set up your WineryXplorer profile to personalize your experience
          </p>
        </div>

        <Card variant="elevated" padding="lg">
          <OnboardingProgress
            steps={steps}
            currentStep={currentStep}
            totalSteps={totalSteps}
          />

          {/* Step Content */}
          <div className="mb-8">
            {currentStep === 1 && (
              <OnboardingStepLocation
                formData={formData}
                errors={errors}
                disabled={isLoading}
                onChange={handleChange}
                onValidateBirthday={validateBirthday}
                onValidateCity={validateCity}
                onValidateState={validateState}
                onValidateCountry={validateCountry}
              />
            )}
            {currentStep === 2 && (
              <OnboardingStepPreferences
                formData={formData}
                disabled={isLoading}
                onChange={handleChange}
              />
            )}
            {currentStep === 3 && !isOAuthUser && (
              <OnboardingStepPhoto
                editorRef={avatarEditorRef}
                userId={currentUser?.id ?? ''}
                currentAvatarUrl={
                  avatarRemoved
                    ? undefined
                    : currentUser?.avatar || currentUser?.image || undefined
                }
                onAvatarChange={(url) => {
                  setAvatarRemoved(!url);
                  // Refresh the auth context so the header updates immediately
                  refetchUser();
                }}
              />
            )}
          </div>

          {/* Navigation Buttons */}
          <OnboardingNavButtons
            currentStep={currentStep}
            totalSteps={totalSteps}
            isOAuthUser={isOAuthUser}
            isLoading={isLoading}
            onBack={handleBack}
            onSkip={handleSkip}
            onNext={handleNext}
            onSubmit={handleSubmit}
          />
        </Card>

        {/* Footer */}
        <div className="mt-6 text-center text-sm text-gray-600">
          <p>
            Need help?{' '}
            <a href="/support" className="text-primary-600 hover:text-primary-700 hover:underline">
              Contact Support
            </a>
          </p>
        </div>
      </div>
    </div>
  );
};
